<template>
  <woot-modal
    :show="show"
    :on-close="close"
    size="medium"
    modal-type="right-aligned"
  >
    <div class="stage-drawer">
      <h3>{{ cardLabel }}</h3>
      <p class="hint">{{ populationLabel }}</p>
      <p v-if="state === 'ready'">
        {{ meta.total }} {{ $t('CAMPAIGN_FLOW.REPORT.CONTACTS') }}
      </p>
      <p v-else-if="state === 'error'" class="error">
        {{ $t('CAMPAIGN_FLOW.REPORT.ERROR') }}
      </p>
      <form class="search" @submit.prevent="applySearch">
        <input
          v-model="search"
          type="search"
          :placeholder="$t('CAMPAIGN_FLOW.REPORT.SEARCH')"
          :disabled="forcedEmpty"
        />
        <woot-button size="small" type="submit" :is-disabled="forcedEmpty">
          {{ $t('CAMPAIGN_FLOW.REPORT.APPLY') }}
        </woot-button>
        <woot-button
          size="small"
          variant="smooth"
          type="button"
          :is-loading="exporting"
          :is-disabled="exportDisabled"
          @click="download('csv')"
        >
          {{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_CSV') }}
        </woot-button>
        <woot-button
          size="small"
          variant="smooth"
          type="button"
          :is-loading="exporting"
          :is-disabled="exportDisabled"
          @click="download('xlsx')"
        >
          {{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_XLSX') }}
        </woot-button>
      </form>
      <p v-if="exportNotice" class="hint">{{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_EMPTY') }}</p>
      <p v-if="exportError" class="error">{{ $t('CAMPAIGN_FLOW.REPORT.EXPORT_ERROR') }}</p>
      <p v-if="state === 'loading'">{{ $t('CAMPAIGN_FLOW.REPORT.LOADING') }}</p>
      <p v-else-if="state === 'ready' && !contacts.length">
        {{ $t('CAMPAIGN_FLOW.REPORT.EMPTY') }}
      </p>
      <ul v-else-if="state === 'ready'" class="contact-list">
        <li v-for="contact in contacts" :key="contact.id">
          <thumbnail
            :src="contact.thumbnail"
            :username="contact.name || ''"
            size="40px"
          />
          <div class="contact-copy">
            <strong>{{ contact.name || '—' }}</strong>
            <span>{{ contact.phone_number || '—' }}</span>
            <small>
              {{ $t('CAMPAIGN_FLOW.REPORT.STAGE_ENTRY') }}:
              {{ formatDate(contact.stage_entered_at) || $t('CAMPAIGN_FLOW.REPORT.NO_STAGE_ENTRY') }}
            </small>
          </div>
        </li>
      </ul>
      <p v-if="state === 'ready' && meta.total_pages">
        {{ $t('CAMPAIGN_FLOW.REPORT.PAGE', { page: meta.page, pages: meta.total_pages }) }}
      </p>
      <table-footer
        v-if="state === 'ready'"
        :current-page="meta.page"
        :page-size="50"
        :total-count="meta.total"
        @page-change="changePage"
      />
    </div>
  </woot-modal>
</template>

<script>
import CampaignsFlowAPI from 'dashboard/api/campaignsFlow';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  components: { TableFooter, Thumbnail },
  props: {
    show: { type: Boolean, default: false },
    campaignId: { type: Number, required: true },
    cardId: { type: String, required: true },
    cardLabel: { type: String, default: '' },
    population: { type: String, default: 'current' },
    baseFilters: { type: Object, default: () => ({}) },
    forcedEmpty: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      appliedSearch: '',
      contacts: [],
      meta: { page: 1, total: 0, total_pages: 0 },
      state: 'loading',
      requestId: 0,
      exporting: false,
      exportError: false,
      exportNotice: false,
    };
  },
  computed: {
    populationLabel() {
      return this.population === 'passed'
        ? this.$t('CAMPAIGN_FLOW.REPORT.PASSED_HINT')
        : this.$t('CAMPAIGN_FLOW.REPORT.NOW_HINT');
    },
    exportDisabled() {
      return this.state !== 'ready' || this.forcedEmpty || !this.meta.total;
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(open) {
        if (open) this.load(1);
      },
    },
    cardId() {
      if (this.show) this.load(1);
    },
    population() {
      if (this.show) this.load(1);
    },
  },
  beforeDestroy() {
    this.requestId += 1;
  },
  methods: {
    close() {
      this.$emit('close');
    },
    params(page) {
      const params = {
        ...this.baseFilters,
        population: this.population,
        status_id: this.cardId,
        page,
        per_page: 50,
      };
      const terms = [this.baseFilters.q, this.appliedSearch]
        .map(term => (term || '').trim())
        .filter(Boolean);
      if (terms.length) params.q = terms;
      else delete params.q;
      return params;
    },
    async load(page) {
      const requestId = ++this.requestId;
      this.exportError = false;
      this.exportNotice = false;
      if (this.forcedEmpty) {
        this.contacts = [];
        this.meta = { page: 1, total: 0, total_pages: 0 };
        this.state = 'ready';
        this.exportNotice = true;
        return;
      }
      this.state = 'loading';
      try {
        const { data } = await CampaignsFlowAPI.executions(this.campaignId, this.params(page));
        if (requestId !== this.requestId) return;
        if (!data || !Array.isArray(data.contacts) || !data.meta) {
          this.state = 'error';
          return;
        }
        this.contacts = data.contacts;
        this.meta = data.meta;
        this.state = 'ready';
        this.exportNotice = !data.meta.total;
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.state = 'error';
      }
    },
    applySearch() {
      this.appliedSearch = this.search.trim();
      this.load(1);
    },
    changePage(page) {
      this.load(page);
    },
    formatDate(value) {
      if (!value) return '';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    async download(format) {
      if (this.exportDisabled) {
        this.exportNotice = true;
        return;
      }
      this.exporting = true;
      this.exportError = false;
      try {
        const response = await CampaignsFlowAPI.exportExecutions(this.campaignId, {
          ...this.params(1),
          format,
        });
        const type = response.headers['content-type'] || '';
        if (type.includes('application/json')) {
          this.exportError = true;
          return;
        }
        const blob = new Blob([response.data], { type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const match = /filename="?([^"]+)"?/.exec(response.headers['content-disposition'] || '');
        link.href = url;
        link.download = match ? match[1] : `relatorio.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.exportError = true;
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.stage-drawer {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-size: 1.4rem;
}
.stage-drawer h3 {
  font-size: 2rem;
  line-height: 1.3;
}
.hint,
.contact-list small {
  color: var(--s-600);
  font-size: 1.3rem;
}
.search {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.search input {
  min-width: 12rem;
  padding: 0.4rem 0.6rem;
}
.contact-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-height: 60vh;
  overflow: auto;
}
.contact-list li {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.8rem 0;
}
.contact-copy {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}
.contact-copy strong {
  font-size: 1.5rem;
  line-height: 1.3;
}
.contact-copy span {
  font-size: 1.35rem;
}
.error {
  color: var(--r-500);
}
</style>
